import { useMemo, useState } from 'react';

import { ICause, useShowCause, useUserData } from 'core/Api';
import { APP_LINKS } from 'core/Utils';
import { useSetAtom } from 'jotai';
import { isIOS } from 'react-device-detect';
import { Navigate, useParams } from 'react-router-dom';
import { ButtonMantine, Container, Flex, Group, Image, Stack, Text, getGradient } from 'ui';

import { HandHeartIcon } from '../../assets/images/icons/HandHeart';
import { onboardingCauseAtom } from '../../atoms/onboarding';
import LearnMoreDrawer from '../../components/Drawers/LearnMoreDrawer';
import useGtmServices from '../../hooks/useGtmServices';
import { useNavigateToAuth } from '../../hooks/useNavigateToAuth/useNavigateToAuth';
import './Referrals.scss';

export type IReferral = {
  cause: ICause;
  first_name?: string;
  last_name?: string;
  uuid: string;
};

type ReferralLayoutProps = {
  cause?: ICause;
  referral?: IReferral;
};

const Mobile = ({ cause: causeInfo }: ReferralLayoutProps) => {
  const GtmService = useGtmServices();
  const { handleNavigateToAuth } = useNavigateToAuth();

  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const setOnboardingCause = useSetAtom(onboardingCauseAtom);
  const referralUrl = window.location.href;
  const { uuid, code } = useParams<{ uuid: string; code: string }>();
  const { data: cause, isLoading } = useShowCause(causeInfo?.uuid);

  const handleEnrollOnWeb = () => {
    setOnboardingCause(cause?.uuid ?? null);
    handleNavigateToAuth({ page: 'sign-up' });
  };

  const playStoreReferralLink = useMemo(() => {
    if (code) {
      return `${APP_LINKS.android_play_store}&referralCode%3D${code}`;
    } else if (uuid) {
      return `${APP_LINKS.android_play_store}&causeID%3D${uuid}`;
    } else {
      return APP_LINKS.android_play_store;
    }
  }, [uuid, code]);

  const handleGetApp = () => {
    navigator.clipboard.writeText(referralUrl?.toLowerCase());
    if (isIOS) {
      window.location.href = APP_LINKS.ios_apple_store;
    } else {
      if (playStoreReferralLink) {
        window.location.href = playStoreReferralLink;
      }
    }
  };

  return (
    <Container
      style={(theme) => ({
        position: 'absolute',
        background: getGradient({ deg: 246.38, from: 'cobalt.8', to: 'cobalt.8' }, theme),
        backgroundColor: theme?.colors.cobalt[6],
        top: 0,
        paddingTop: '122px',
        paddingBottom: '24px',
        minHeight: '100vh',
      })}
    >
      <Stack gap={48} align="center">
        <Text c="tangerine" fz="32px" fw={700} ta="center">
          {cause
            ? `Support ${cause?.name ?? 'your cause'} for Free with Givebacks`
            : 'Welcome to Givebacks!'}
        </Text>
        <Flex
          justify="center"
          align="center"
          style={(theme) => ({
            borderRadius: theme.radius.md,
            width: '150px',
            height: '150px',
            background: 'white',
            position: 'relative',
            overflow: 'hidden',
          })}
        >
          <Image
            style={{ width: '150px', height: '150px' }}
            src={cause?.thumbnail?.url}
            isDataLoading={isLoading && !!causeInfo}
            className={'m-auto'}
          >
            <HandHeartIcon className="w-[115px] h-[115px] absolute top-[-4px] right-[0px]" />
          </Image>
        </Flex>
        <Stack gap="md" align="center">
          <Text fz="24px" fw={700} c="white">
            Join Today!
          </Text>
          <Text fz="md" ta="center" c="white">{`${
            cause?.name ?? 'Your cause'
          } will earn donations funded by shops and restaurants you know and love when you use Givebacks!`}</Text>
        </Stack>
        <Stack w="100%">
          <ButtonMantine
            variant="gradient"
            onClick={() =>
              GtmService.click(handleGetApp, {
                type: 'button',
                action: 'download_app',
                label: 'Get the App',
              })
            }
            fullWidth
          >
            Get the App
          </ButtonMantine>
          <ButtonMantine
            color="tangerine"
            variant="outline"
            onClick={() =>
              GtmService.click(handleEnrollOnWeb, {
                type: 'button',
                action: 'sign_up',
                label: 'Enroll on Web',
              })
            }
            fullWidth
          >
            Enroll on web
          </ButtonMantine>

          <ButtonMantine
            color="white"
            variant="transparent"
            onClick={() =>
              GtmService.click(() => setIsLearnMoreOpen(true), {
                type: 'button',
                action: 'info',
                label: 'Learn More',
              })
            }
            size="medium"
          >
            <Group gap="3">
              <Text td="underline" span>
                Learn more
              </Text>
              <Text c="white" td="none" span>
                about Givebacks
              </Text>
            </Group>
          </ButtonMantine>
        </Stack>
      </Stack>
      <LearnMoreDrawer
        isLearnMoreOpen={isLearnMoreOpen}
        setIsLearnMoreOpen={setIsLearnMoreOpen}
        handleEnrollOnWeb={handleEnrollOnWeb}
        handleGetApp={handleGetApp}
      />
    </Container>
  );
};

const Desktop = () => {
  const { data: user } = useUserData();

  if (user) {
    return <Navigate to="/offers" />; // Might be better to just send them to the cause page?? maybe.
  }

  return null;
};

export const ReferralLayout = {
  Mobile,
  Desktop,
};
